import ExpandMore from '@mui/icons-material/ExpandMore';
import Moving from '@mui/icons-material/Moving';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { Subscription } from '@rails/actioncable';
import { AiJobSummaryChannel_Read, WaypointType } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';

import Label from '~components/FormFields/v2/Label';
import { CommonLoader } from '~components/Loaders/CommonLoader';
import { AiJobSummary, AiLoadSummary } from '~hooks/useAiSummaries/models';
import useAiSummaries from '~hooks/useAiSummaries/useAiSummaries';
import { Job } from '~hooks/useJob/models';
import type { LocationHistoryMinimal } from '~hooks/useLocationHistory/models';
import { getLocationHistoriesByMovingSite } from '~hooks/useLocationHistory/useLocationHistory';
import { isDefaultSiteUsages, type JobSiteUsage } from '~hooks/useSiteUsages/models';
import {
  buildTimedPath,
  interpolatePositionAtTime,
} from '~pages/Admin/Geo/Distances/DistanceMap/catmullRomSpline';
import { createWaypointSvgDataUrl } from '~pages/Admin/Geo/mapIcons';
import { realTimeChannels } from '~services/realTimeChannels';
import { useStores } from '~store';
import { subscribeToChannel } from '~utils/rtu/rtu-utils';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import { AiSiteFocus, buildAiSummaryCircles } from '../Geofence/aiSummaryCircles';
import {
  buildWaypointMarkers,
  getMovingSiteInfo,
  getTotalLoadCount,
} from '../Geofence/derivations';
import {
  buildSiteOverridesFromSiteUsages,
  SiteOverride,
} from '../Geofence/geofence-utils';
import { GeofenceDebugMap } from '../Geofence/GeofenceDebugMap';
import { GeofenceTimeline } from '../Geofence/GeofenceTimeline';
import { SiteOverridePanel } from '../Geofence/SiteOverridePanel';
import { AiSummaryDetails } from './AiSummaryDetails';
import { AiSummaryFieldList } from './AiSummaryFieldList';
import { AiSummaryFindings } from './AiSummaryFindings';
import { AiSummarySites } from './AiSummarySites';

// keeps the map usable even when the AI summary sidebar has little content
// (e.g. the not-processed empty state)
const MAP_ROW_MIN_HEIGHT_PX = 360;

interface AiSummaryPaneProps {
  job: Job | null;
  jobId: string;
  companyId: string | null;
  isFeet: boolean;
  siteUsages: JobSiteUsage[];
  pings: LocationHistoryMinimal[];
}

const AiSummaryPane = ({
  job,
  jobId,
  companyId,
  isFeet,
  siteUsages,
  pings,
}: AiSummaryPaneProps) => {
  const { fetchAllAiLoadSummaries, generateAiJobSummary, getAiJobSummary } =
    useAiSummaries();
  const [aiJobSummary, setAiJobSummary] = useState<AiJobSummary | null>(null);
  const [aiLoadSummaries, setAiLoadSummaries] = useState<AiLoadSummary[]>([]);
  const [isAiSummaryLoading, setIsAiSummaryLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationErrorMessage, setGenerationErrorMessage] = useState<string | null>(
    null,
  );
  // mirrors isGenerating so the long-lived RTU handler can read the current value
  const isGeneratingRef = useRef(false);
  isGeneratingRef.current = isGenerating;
  const aiSummaryRTUSubscription = useRef<Subscription | null>(null);
  // bumped whenever the channel applies a pushed summary: fetches that started
  // before the push carry an older snapshot and must abandon their writes
  const pushedSummaryVersionRef = useRef(0);

  useEffect(() => {
    if (!companyId) return;

    const controller = new AbortController();
    const { signal } = controller;
    const loadAiSummaries = async () => {
      const versionAtStart = pushedSummaryVersionRef.current;
      setIsAiSummaryLoading(true);
      try {
        const [jobSummary, loadSummaries] = await Promise.all([
          getAiJobSummary(companyId, jobId, signal),
          fetchAllAiLoadSummaries(companyId, jobId, signal),
        ]);
        const pushArrivedMidFetch = pushedSummaryVersionRef.current !== versionAtStart;
        if (signal.aborted || pushArrivedMidFetch) return;
        setAiJobSummary(jobSummary);
        setAiLoadSummaries(loadSummaries);
      } catch {
        // the hook already surfaced the error toast
      } finally {
        if (!signal.aborted) {
          setIsAiSummaryLoading(false);
        }
      }
    };
    void loadAiSummaries();

    return () => controller.abort();
  }, [companyId, jobId, getAiJobSummary, fetchAllAiLoadSummaries]);

  const refreshAiLoadSummaries = useCallback(async () => {
    if (!companyId) return;

    const versionAtStart = pushedSummaryVersionRef.current;
    try {
      const loadSummaries = await fetchAllAiLoadSummaries(companyId, jobId);
      const newerPushArrivedMidFetch = pushedSummaryVersionRef.current !== versionAtStart;
      if (newerPushArrivedMidFetch) return;
      setAiLoadSummaries(loadSummaries);
    } catch {
      // the hook already surfaced the error toast
    }
  }, [companyId, jobId, fetchAllAiLoadSummaries]);

  const subscribeToAiSummaryRTU = useCallback(() => {
    subscribeToChannel(
      realTimeChannels.AiJobSummaryChannel,
      // the channel is job-scoped: it subscribes with job_id instead of company_id
      null,
      (sub) => (aiSummaryRTUSubscription.current = sub),
      (payload: AiJobSummaryChannel_Read) => {
        if (payload.data?.job_id === jobId) {
          pushedSummaryVersionRef.current += 1;
          setAiJobSummary(AiJobSummary.parse(payload.data));
          setIsGenerating(false);
          setGenerationErrorMessage(null);
          // a run re-deduces the trips; reload them so the map circles and the
          // details table match the pushed summary
          refreshAiLoadSummaries();
          return;
        }

        // exception payloads carry only error/message. the channel also transmits
        // one when a subscription is rejected, so only a failure the user is
        // actively waiting on surfaces in the pane
        if (payload.error && isGeneratingRef.current) {
          setIsGenerating(false);
          setGenerationErrorMessage(
            payload.message ?? t('error_messages.ai_summaries.failed_to_generate'),
          );
        }
      },
      { job_id: jobId },
    );
  }, [jobId, refreshAiLoadSummaries]);

  useEffect(() => {
    if (!aiSummaryRTUSubscription.current) {
      void subscribeToAiSummaryRTU();
    }

    return () => {
      aiSummaryRTUSubscription.current?.unsubscribe();
      aiSummaryRTUSubscription.current = null;
    };
  }, [subscribeToAiSummaryRTU]);

  const handleGenerateAiSummary = useCallback(async () => {
    if (!companyId) return;

    setIsGenerating(true);
    setGenerationErrorMessage(null);
    try {
      await generateAiJobSummary(companyId, jobId);
    } catch {
      // the request was not accepted, so no RTU push is coming to clear the spinner
      setIsGenerating(false);
      setGenerationErrorMessage(t('error_messages.ai_summaries.failed_to_generate'));
    }
  }, [companyId, jobId, generateAiJobSummary]);

  const aiSummaryCircles = useMemo(
    () => buildAiSummaryCircles(aiLoadSummaries),
    [aiLoadSummaries],
  );

  // --- Read-only geofence display (mirrors the Geofence page, minus editing) ---
  const [siteOverrides, setSiteOverrides] = useState<SiteOverride[]>([]);
  const siteOverridesRef = useRef<SiteOverride[]>([]);
  siteOverridesRef.current = siteOverrides;
  const [locationTab, setLocationTab] = useState<number | null>(null);
  const [focusedAiSite, setFocusedAiSite] = useState<AiSiteFocus | null>(null);
  const [indicatorTime, setIndicatorTime] = useState<number | null>(null);
  const [movingSitePings, setMovingSitePings] = useState<LocationHistoryMinimal[]>([]);

  useEffect(() => {
    setLocationTab(null);
    setFocusedAiSite(null);
    setSiteOverrides(
      siteUsages.length === 0 ? [] : buildSiteOverridesFromSiteUsages(siteUsages),
    );
  }, [siteUsages]);

  /** Expanding an AI site and selecting a platform site are mutually exclusive on the map. */
  const handleToggleAiSite = useCallback((site: AiSiteFocus | null) => {
    setFocusedAiSite(site);
    if (site) {
      setLocationTab(null);
    }
  }, []);

  const parsedJobSites = useMemo(() => siteUsages.map((su) => su.site), [siteUsages]);
  const totalLoadCount = useMemo(() => getTotalLoadCount(siteUsages), [siteUsages]);
  const movingSiteInfo = useMemo(() => getMovingSiteInfo(siteUsages), [siteUsages]);

  useEffect(() => {
    if (!jobId || !companyId || !movingSiteInfo) {
      setMovingSitePings([]);
      return;
    }
    const controller = new AbortController();
    getLocationHistoriesByMovingSite(
      companyId,
      jobId,
      movingSiteInfo.movingSiteId,
      controller.signal,
    )
      .then((result) => {
        if (!controller.signal.aborted) setMovingSitePings(result);
      })
      .catch(() => {});
    return () => controller.abort();
  }, [jobId, companyId, movingSiteInfo]);

  const timedPath = useMemo(() => buildTimedPath(pings), [pings]);
  const movingSiteTimedPath = useMemo(
    () => buildTimedPath(movingSitePings),
    [movingSitePings],
  );

  const truckMarker = useMemo(() => {
    if (indicatorTime === null) return null;
    return interpolatePositionAtTime(timedPath, indicatorTime);
  }, [indicatorTime, timedPath]);

  const movingSitePosition = useMemo(() => {
    if (indicatorTime === null || !movingSiteInfo) return null;
    const { rawPath, pingTimes } = movingSiteTimedPath;
    if (rawPath.length === 0) return null;

    // clamp to nearest endpoint so the geofence stays visible even when the
    // scrubber is outside the moving site's time range
    const clampedTime = Math.max(
      pingTimes[0],
      Math.min(indicatorTime, pingTimes[pingTimes.length - 1]),
    );
    const result = interpolatePositionAtTime(movingSiteTimedPath, clampedTime);
    if (!result) return null;
    return { position: result.position, radius: movingSiteInfo.radius };
  }, [indicatorTime, movingSiteTimedPath, movingSiteInfo]);

  /** All geofences for the debug map, with the selected one marked. */
  const debugMapGeofences = useMemo(() => {
    return siteOverrides.flatMap((site, siteIndex) => {
      const isMovingSite =
        movingSiteInfo !== null && siteIndex === movingSiteInfo.siteIndex;
      const isDefaultSite = isDefaultSiteUsages(site.usages ?? []);
      return site.geofences.map((gf, i) => {
        const movingCenter =
          isMovingSite && movingSitePosition
            ? {
                lat: movingSitePosition.position.lat,
                lng: movingSitePosition.position.lng,
              }
            : null;
        return {
          key: `${siteIndex}-${gf.key}`,
          type: gf.geofenceType,
          tag: gf.tag,
          polygonCoordinates: gf.polygonCoordinates,
          circleCenter: movingCenter ?? gf.circleCenter,
          circleRadius: gf.circleRadius,
          isSelected: siteIndex === locationTab && i === site.selectedGeofenceIndex,
          isMoving: isMovingSite,
          isDefaultSite,
        };
      });
    });
  }, [siteOverrides, locationTab, movingSiteInfo, movingSitePosition]);

  const waypointMarkers = useMemo(
    () => buildWaypointMarkers(siteUsages, movingSiteInfo, movingSitePosition),
    [siteUsages, movingSiteInfo, movingSitePosition],
  );

  const handleGeofenceMapClick = useCallback((compositeKey: string) => {
    // Keys are formatted as `${siteIndex}-${gf.key}` in debugMapGeofences
    for (let siteIndex = 0; siteIndex < siteOverridesRef.current.length; siteIndex++) {
      const site = siteOverridesRef.current[siteIndex];
      const idx = site.geofences.findIndex(
        (gf) => `${siteIndex}-${gf.key}` === compositeKey,
      );
      if (idx !== -1) {
        setLocationTab(siteIndex);
        setFocusedAiSite(null);
        setSiteOverrides((prev) =>
          prev.map((s, i) =>
            i === siteIndex ? { ...s, selectedGeofenceIndex: idx } : s,
          ),
        );
        return;
      }
    }
  }, []);

  const setWaypointOverride = useCallback(
    (
      waypointIndex: number,
      update: Partial<SiteOverride> | ((prev: SiteOverride) => SiteOverride),
    ) => {
      setSiteOverrides((prev) =>
        prev.map((s, i) => {
          if (i !== waypointIndex) return s;
          return typeof update === 'function' ? update(s) : { ...s, ...update };
        }),
      );
    },
    [],
  );

  /** View-only: highlight a different geofence on the map. */
  const selectWaypointGeofence = useCallback((waypointIndex: number, gfIndex: number) => {
    setSiteOverrides((prev) => {
      const site = prev[waypointIndex];
      if (!site || site.selectedGeofenceIndex === gfIndex) return prev;
      return prev.map((s, i) =>
        i === waypointIndex ? { ...s, selectedGeofenceIndex: gfIndex } : s,
      );
    });
  }, []);

  // the geofence display is read-only on this page; there are no trigger
  // recalculations to run after edits
  const noopLoadTriggers = useCallback(() => {}, []);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        p: 2,
        gap: 2,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: 2,
          flex: '0 0 auto',
          position: 'relative',
          minHeight: MAP_ROW_MIN_HEIGHT_PX,
        }}
      >
        {/* The sidebar expands to its full content height (no internal scrolling);
            the map row grows and shrinks with it. */}
        <Box
          sx={{
            width: 320,
            flexShrink: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <AiSummaryFieldList
            aiJobSummary={aiJobSummary}
            aiLoadSummaryCount={aiLoadSummaries.length}
            generationErrorMessage={generationErrorMessage}
            isGenerating={isGenerating}
            isLoading={isAiSummaryLoading}
            onGenerate={handleGenerateAiSummary}
            platformLoadCount={job?.completedLoadsCount ?? 0}
          />

          {!isAiSummaryLoading && (
            <AiSummaryFindings findings={aiJobSummary?.findings ?? []} />
          )}

          {!isAiSummaryLoading && (
            <AiSummarySites
              circles={aiSummaryCircles}
              expandedSiteKey={focusedAiSite?.key ?? null}
              onToggleSite={handleToggleAiSite}
            />
          )}

          {/* No flex/overflow constraints here (unlike the Geofence page): with the
              row height capped, a flexible box would be squeezed to zero height by
              the content above it. The column itself scrolls instead. */}
          {job && siteOverrides.length > 0 && (
            <Box>
              <Label sx={{ mb: 0.5, pl: '9px' }}>
                {t('admin.job_geo_debug.ai_summary_platform_sites_label')}
              </Label>
              {siteOverrides.map((so, i) => (
                <Accordion
                  key={siteUsages[i]?.site.id ?? i}
                  expanded={locationTab === i}
                  onChange={(_, expanded) => {
                    setLocationTab(expanded ? i : null);
                    if (expanded) {
                      setFocusedAiSite(null);
                    }
                  }}
                  disableGutters
                  sx={{
                    boxShadow: 'none',
                    '&:before': { display: 'none' },
                    border: (theme) => `1px solid ${theme.palette.divider}`,
                    '&:not(:first-of-type)': { borderTop: 0 },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMore />}
                    sx={{
                      minHeight: 36,
                      px: 1,
                      '& .MuiAccordionSummary-content': {
                        my: 0.5,
                        minWidth: 0,
                        overflow: 'hidden',
                        alignItems: 'center',
                        gap: 0.75,
                      },
                    }}
                  >
                    <img
                      src={createWaypointSvgDataUrl(
                        siteUsages[i]?.usages.length > 1
                          ? 'both'
                          : (siteUsages[i]?.usages[0]?.waypointType ??
                              WaypointType.PICKUP),
                        isDefaultSiteUsages(siteUsages[i]?.usages ?? []),
                      )}
                      alt=""
                      width={18}
                      height={18}
                      style={{ flexShrink: 0 }}
                    />
                    <Typography
                      variant="body2"
                      noWrap
                      sx={{
                        fontWeight: locationTab === i ? 600 : 400,
                        flex: 1,
                      }}
                    >
                      {so.name}
                    </Typography>
                    {so.geofences.some((gf) => gf.movingGeofence) && (
                      <Moving
                        sx={{
                          fontSize: '1rem',
                          color: 'text.secondary',
                          flexShrink: 0,
                        }}
                      />
                    )}
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 0, pb: 1, px: 1 }}>
                    <SiteOverridePanel
                      override={so}
                      setOverride={(update) => setWaypointOverride(i, update)}
                      onSelectGeofence={(gfIndex) => selectWaypointGeofence(i, gfIndex)}
                      jobSite={parsedJobSites[i] ?? null}
                      isEditable={false}
                      isFeet={isFeet}
                      loadTriggers={noopLoadTriggers}
                      showUsages
                      totalLoadCount={totalLoadCount}
                    />
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          )}
        </Box>

        <Box
          sx={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: (theme) => `calc(320px + ${theme.spacing(2)})`,
            right: 0,
          }}
        >
          <GeofenceDebugMap
            geofences={debugMapGeofences}
            aiSummaryCircles={aiSummaryCircles}
            focusedAiSite={focusedAiSite}
            waypoints={waypointMarkers}
            pings={pings}
            truckMarker={truckMarker}
            onGeofenceClick={handleGeofenceMapClick}
            sx={{ height: '100%' }}
          />
        </Box>
      </Box>

      <Box sx={{ mt: -3.25, mb: -1 }}>
        <GeofenceTimeline pings={pings} onTimeChange={setIndicatorTime} />
      </Box>

      <Box
        sx={{
          width: '100%',
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
        }}
      >
        <AiSummaryDetails aiJobSummary={aiJobSummary} aiLoadSummaries={aiLoadSummaries} />
      </Box>
    </Box>
  );
};

/**
 * Job ID is from the URL (stable for this page). Company ID is always the user's company.
 */
export const AiSummary = observer(function AiSummary() {
  const { jobId } = useParams<{ jobId: string }>();
  const { userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);
  const { job, siteUsages, pings, isMapDataLoading } = useOutletContext<{
    job: Job | null;
    siteUsages: JobSiteUsage[];
    pings: LocationHistoryMinimal[];
    isMapDataLoading: boolean;
  }>();
  const isLoadingJob = !job || isMapDataLoading;

  if (!jobId) {
    return (
      <Typography sx={{ color: 'text.secondary' }}>
        {t('admin.job_geo_debug.missing_job_id')}
      </Typography>
    );
  }

  if (isLoadingJob) {
    return <CommonLoader />;
  }

  const isFeet = userStore?.userCompany?.isFeet ?? false;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          flex: 1,
          minHeight: 0,
          minWidth: 0,
          border: (theme) => `1px solid ${theme.palette.divider}`,
          borderRadius: 1,
          overflow: 'hidden',
        }}
      >
        {/* keyed by job so navigating between jobs remounts the pane: generation
            state and the channel subscription are job-scoped and must not carry
            over (a run started on the previous job would leave a stuck spinner) */}
        <AiSummaryPane
          key={jobId}
          job={job}
          jobId={jobId}
          companyId={companyId}
          isFeet={isFeet}
          siteUsages={siteUsages}
          pings={pings}
        />
      </Box>
    </Box>
  );
});
